import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleMinus, faFaceThinking, faThumbsDown, faThumbsUp } from "@fortawesome/pro-regular-svg-icons";
import { RightToWorkCheckStatusId } from "../../services/ArtistService";

export interface RightToWorkDisplay {
    label: string;
    icon: typeof faThumbsUp;
    colorClass: string;
}

/**
 * Maps a Right To Work check result id to the label / icon / colour shown in the UI.
 *   Passed (20)                      -> green thumbs up
 *   Expired (30), Failed (99)        -> red thumbs down
 *   Pending (5), Superseded (40)     -> amber
 *   Not Required (1)                 -> grey
 * Anything else (no check recorded yet) is shown as a grey "Not Checked".
 */
export const getRightToWorkDisplay = (checkStatusId?: string | number | null): RightToWorkDisplay => {
    switch ((checkStatusId ?? "").toString()) {
        case RightToWorkCheckStatusId.Passed:
            return { label: "Passed", icon: faThumbsUp, colorClass: "text-success" };
        case RightToWorkCheckStatusId.Expired:
            return { label: "Expired", icon: faThumbsDown, colorClass: "text-danger" };
        case RightToWorkCheckStatusId.Failed:
            return { label: "Failed", icon: faThumbsDown, colorClass: "text-danger" };
        case RightToWorkCheckStatusId.Pending:
            return { label: "Pending", icon: faFaceThinking, colorClass: "text-warning" };
        case RightToWorkCheckStatusId.Superseded:
            return { label: "Superseded", icon: faFaceThinking, colorClass: "text-warning" };
        case RightToWorkCheckStatusId.RetryViaApp:
            return { label: "Retry Via App Requested", icon: faFaceThinking, colorClass: "text-warning" };
        case RightToWorkCheckStatusId.NotRequired:
            return { label: "Not Required", icon: faCircleMinus, colorClass: "text-muted" };
        default:
            return { label: "Not Checked", icon: faCircleMinus, colorClass: "text-muted" };
    }
};

interface RightToWorkStatusProps {
    /** Right To Work check status id (e.g. "20"). Empty / unknown shows "Not Checked". */
    checkStatusId?: string | number | null;
}

/**
 * Renders "[status icon] Right to Work: <status>".
 * Meant to sit inside an existing icon + text row such as <div className="artist-profile__contact-detail">.
 */
const RightToWorkStatus: React.FC<RightToWorkStatusProps> = ({ checkStatusId }) => {
    const { label, icon, colorClass } = getRightToWorkDisplay(checkStatusId);
    return (
        <>
            <FontAwesomeIcon icon={icon} className={colorClass} aria-hidden="true" />
            <span>Right to Work: {label}</span>
        </>
    );
};

export default RightToWorkStatus;